// BundleCustomizationModal.tsx
import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Typography, IconButton, RadioGroup, FormControlLabel, Radio,
  Checkbox, FormGroup, Box
} from '@mui/material';
import { AddCircleOutline, RemoveCircleOutline } from '@mui/icons-material';
import {
  BaseItem, MenuItem, ItemVariant, IngredientOption,
  FullMenuPageData, ComboVariant, bundleViewData, BaseItemVariants, BaseItemOptions,
  findBaseItemAndVariant
} from '../../models/fullMenuPageData';
import { computeBaseItemSelectionPriceVariation, getPriceOfMenuCart, MenuCartItem, SelectedIngredientOption, toMenuCart } from '../../models/fullMenuCart';
import { formatMoney } from '../../services/strings/formatters';
import { INVALID_MENU } from '../../models/errors';
import { Bundle } from '../../profilePage/maintainSpecificRestaurant/upsertFullMenuHelpers';

interface BundleCustomizationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAddToCart: (item: MenuCartItem[]) => void;
  bundle: bundleViewData;
  menuData: FullMenuPageData;
}

interface NormalizedBundleItem {
  id: string;
  originalItemId: string;
  menuItem: MenuItem;
  baseItem: BaseItem | undefined;
  bundlePrice: number;
  comboVariants: ComboVariant[];
  bundleQuantity: number;
  noVariantPriceOverride: number | undefined;
  variantPriceOverrides: {
    id: string;
    variantId: string;
    price: number;
  }[];
}


const isComboVariantSoldOut = (comboVariant: ComboVariant, menuData: FullMenuPageData): boolean => {
  for (const cv of comboVariant.itemVariantIds) {
    const comboVariantItem = menuData.baseItems.find(bi => bi.id === cv)
    if (!comboVariantItem || comboVariantItem.isSoldOut) {
      return true
    }
  }
  return false
}

/** Flatten option groups into a SelectedIngredientOption[] */
function flattenOptionsToSelectedIngredientOptions(optGroups: BaseItemOptions[] | undefined): SelectedIngredientOption[] {
  if (!optGroups) return [];
  const out: SelectedIngredientOption[] = [];
  for (const g of optGroups) {
    for (const o of g.options) out.push({ option: o, quantity: 0 });
  }
  return out;
}

/* --- Small helper component: individual base item controls --- */
function ManageIndividualItem({
  baseItem,
  menuItem,
  variantsByGroup,
  setVariantForGroup,
  SelectedIngredientOptions,
  handleIngredientChange,
}: {
  baseItem: BaseItem;
  menuItem: MenuItem;
  variantsByGroup: Record<string, ItemVariant | undefined>;
  setVariantForGroup: (groupId: string, v: ItemVariant | undefined) => void;
  SelectedIngredientOptions: SelectedIngredientOption[];
  handleIngredientChange: (ingredientId: string, checked: boolean) => void;
}) {
  if (!baseItem) return null;

  return (
    <>
      {/* Variant groups */}
      {baseItem.variants.map((group: BaseItemVariants) => (
        <Box key={group.id} sx={{ mb: 1 }}>
          <Typography variant="subtitle1">{group.name}</Typography>
          {group.description && <Typography variant="caption" display="block">{group.description}</Typography>}
          <RadioGroup
            value={variantsByGroup[group.id]?.id || ''}
            onChange={(e) => {
              const chosen = group.variants.find(v => v.id === e.target.value);
              setVariantForGroup(group.id, chosen);
            }}
          >
            {group.variants.map(variant => {
              if (!variant.isSoldOut) {
                return <FormControlLabel
                  key={variant.id}
                  value={variant.id}
                  control={<Radio />}
                  label={`${variant.name} - (${formatMoney((menuItem.isCombo ? variant.comboPrice : variant.price) / 100)})`}
                />
              }
              return <Typography variant="subtitle1" sx={{ textDecoration: 'line-through' }}>
                {`X ${variant.name} - (${formatMoney((menuItem.isCombo ? variant.comboPrice : variant.price) / 100)}) - sold out`}</Typography>
            })}
          </RadioGroup>
        </Box>
      ))}

      {/* Option groups */}
      {baseItem.options.map((group: BaseItemOptions) => (
        <Box key={group.id} sx={{ mt: 1 }}>
          <Typography variant="subtitle1">{group.name}</Typography>
          {group.description && <Typography variant="caption" display="block">{group.description}</Typography>}
          <FormGroup>
            {group.options.map(option => {
              if (!option.isSoldOut) {
                return <FormControlLabel
                  key={option.id}
                  control={
                    <Checkbox
                      checked={SelectedIngredientOptions.some(si => si.option.id === option.id && si.quantity > 0)}
                      onChange={(e) => handleIngredientChange(option.id, e.target.checked)}
                    />
                  }
                  label={`${option.name} (+${formatMoney(option.price / 100)})`}
                />
              }
              return <Typography variant="subtitle1" sx={{ textDecoration: 'line-through' }}>
                {`X ${option.name} (+${formatMoney(option.price / 100)}) - sold out`}</Typography>
            })}
          </FormGroup>
        </Box>
      ))}
    </>
  );
}

/* --- Renders the sub-items of a comboVariant, using per-sub-state --- */
function ManageComboVariant({
  comboVariant,
  menuData,
  perSubStates,
  onSetSubVariant,
  onToggleSubIngredient,
  menuItem,
}: {
  comboVariant: ComboVariant;
  menuData: FullMenuPageData;
  perSubStates: {
    baseItem?: BaseItem;
    variantsByGroup: Record<string, ItemVariant | undefined>;
    SelectedIngredientOptions: SelectedIngredientOption[];
  }[];
  onSetSubVariant: (subIndex: number, groupId: string, v: ItemVariant | undefined) => void;
  onToggleSubIngredient: (subIndex: number, ingredientId: string, checked: boolean) => void;
  menuItem: MenuItem;
}) {
  return (
    <>
      {comboVariant.itemVariantIds.map((ivID, subIdx) => {
        const resolved = findBaseItemAndVariant(menuData, ivID);
        const baseItem = resolved.baseItem;
        if (!baseItem) return null;
        const state = perSubStates[subIdx] || { variantsByGroup: {}, SelectedIngredientOptions: [] };

        return (
          <div key={ivID} style={{ borderTop: '1px solid #eee', marginTop: '0.5rem', paddingTop: '0.5rem' }}>
            <Box sx={{ display: "flex", flexDirection: "row", justifyContent: "space-between", alignItems: "center" }}>
              <Typography variant="h6">{baseItem.name}</Typography>
              {baseItem.imageUrl && <img src={baseItem.imageUrl} alt="Preview" style={{ width: '128px', height: '128px' }} />}
            </Box>
            <ManageIndividualItem
              baseItem={baseItem}
              menuItem={menuItem}
              variantsByGroup={state.variantsByGroup}
              setVariantForGroup={(groupId, v) => onSetSubVariant(subIdx, groupId, v)}
              SelectedIngredientOptions={state.SelectedIngredientOptions}
              handleIngredientChange={(ingredientId, checked) => onToggleSubIngredient(subIdx, ingredientId, checked)}
            />
          </div>
        );
      })}
    </>
  );
}

export function BundleCustomizationModal({
  isOpen, onClose, onAddToCart, bundle, menuData
}: BundleCustomizationModalProps) {
  const [quantity, setQuantity] = useState<number>(1);
  const [showValidationAttempt, setShowValidationAttempt] = useState(false);

  // Normalize bundle items to include handy refs
  const normalizedItems: NormalizedBundleItem[] = useMemo(() => {
    if (!bundle?.items) return [];

    return bundle.items.flatMap(it => {
      const menuItem = it.menuItem;
      const baseItem = menuData.baseItems.find(b => b.id === menuItem.baseItemId);
      if (!baseItem) {
        throw INVALID_MENU()
      }
      const comboVariants = menuItem.isCombo ? (menuItem.comboVariants || []) : [];

      const qty = Math.max(1, Number(it.quantity) || 1);
      let bundlePrice = 0
      if (it.variantPriceOverrides.length == 0 && it.noVariantPriceOverride) {
        bundlePrice = it.noVariantPriceOverride
      } else {
        // find cheapest combo
        let cheapest: number | null = null
        comboVariants.forEach(cv => {
          const priceOverride = it.variantPriceOverrides.find(val => val.variantId == cv.id)
          if (priceOverride && (cheapest == null || priceOverride.price < cheapest)) {
            cheapest = priceOverride.price
          }
        })
        if (cheapest) {
          bundlePrice = cheapest
        } else {
          bundlePrice = baseItem.price
        }
      }

      return Array.from({ length: qty }, (_, instanceIndex) => ({
        // unique instance id, but keep originalItemId for reversing the expansion
        id: `${it.id}::${instanceIndex}}`,
        originalItemId: it.id,
        menuItem,
        baseItem,
        comboVariants,
        bundlePrice,
        // each expanded entry represents a single unit
        bundleQuantity: 1,
        noVariantPriceOverride: it.noVariantPriceOverride,
        // copy overrides so instances don't share the same array reference
        variantPriceOverrides: (it.variantPriceOverrides || []).map(v => ({ ...v }))
      }));
    });
  }, [bundle, menuData]);

  /* 
    perItemState layout:
     - for non-combo items: { type: 'single', baseItem, variantsByGroup: Record<groupId, ItemVariant>, SelectedIngredientOptions: SelectedIngredientOption[] }
     - for combo items: { type: 'combo', selectedComboVariantId?: string, subStates: [ { baseItem, variantsByGroup, SelectedIngredientOptions } ] }
  */
  const [perItemState, setPerItemState] = useState<any[]>(() => {
    return normalizedItems.map(n => {
      if (n.menuItem.isCombo) {
        return { type: 'combo' as const, selectedComboVariantId: undefined as string | undefined, subStates: [] as any[] };
      } else {
        const base = n.baseItem as BaseItem | undefined;
        const variantsByGroup: Record<string, ItemVariant | undefined> = {};
        if (base) {
          for (const g of base.variants) variantsByGroup[g.id] = g.variants?.[0];
        }
        const SelectedIngredientOptions = flattenOptionsToSelectedIngredientOptions(base?.options);
        return { type: 'single' as const, baseItem: base, variantsByGroup, SelectedIngredientOptions };
      }
    });
  });

  // Re-init conservatively when normalizedItems length changes (preserve user work when possible)
  useEffect(() => {
    setPerItemState(prev => {
      if (prev.length !== normalizedItems.length) {
        return normalizedItems.map(n => {
          if (n.menuItem.isCombo) {
            return { type: 'combo' as const, selectedComboVariantId: undefined as string | undefined, subStates: [] as any[] };
          } else {
            const base = n.baseItem as BaseItem | undefined;
            const variantsByGroup: Record<string, ItemVariant | undefined> = {};
            if (base) for (const g of base.variants) variantsByGroup[g.id] = g.variants?.[0];
            const SelectedIngredientOptions = flattenOptionsToSelectedIngredientOptions(base?.options);
            return { type: 'single' as const, baseItem: base, variantsByGroup, SelectedIngredientOptions };
          }
        });
      }
      return prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [normalizedItems.length]);

  /* --- build subStates for chosen combo while preserving previous choices by baseItem.id --- */
  const buildSubStatesForCombo = (combo: ComboVariant, oldSubStates: any[] = []) => {
    const oldByBaseId = new Map<string, any>();
    oldSubStates.forEach((s: any) => { if (s.baseItem?.id) oldByBaseId.set(s.baseItem.id, s); });

    return combo.itemVariantIds.map(ivID => {
      const resolved = findBaseItemAndVariant(menuData, ivID);
      const base = resolved.baseItem;
      const defaultVariantsByGroup: Record<string, ItemVariant | undefined> = {};
      if (base) for (const g of base.variants) defaultVariantsByGroup[g.id] = g.variants?.[0];

      const defaultIngredients = flattenOptionsToSelectedIngredientOptions(base?.options);
      const matched = base && oldByBaseId.get(base.id);
      if (matched) {
        // preserve matched selections
        return {
          baseItem: base,
          variantsByGroup: matched.variantsByGroup || defaultVariantsByGroup,
          SelectedIngredientOptions: matched.SelectedIngredientOptions?.length ? matched.SelectedIngredientOptions : defaultIngredients
        };
      }
      // if ivID pointed directly at a variant, pre-select it in the correct group
      if (base && resolved.variant && resolved.groupId) {
        const vb = { ...defaultVariantsByGroup, [resolved.groupId]: resolved.variant };
        return { baseItem: base, variantsByGroup: vb, SelectedIngredientOptions: defaultIngredients };
      }
      return { baseItem: base, variantsByGroup: defaultVariantsByGroup, SelectedIngredientOptions: defaultIngredients };
    });
  };

  /* --- functions to mutate perItemState --- */

  const setSelectedComboVariant = (itemIdx: number, comboVariantId: string | undefined) => {
    setPerItemState(prev => {
      const next = [...prev];
      const s = next[itemIdx];
      if (s?.type === 'combo') {
        const normalized = normalizedItems[itemIdx];
        const chosen = normalized.comboVariants.find((cv: ComboVariant) => cv.id === comboVariantId);
        const oldSubStates = s.subStates || [];
        if (chosen) {
          s.selectedComboVariantId = comboVariantId;
          s.subStates = buildSubStatesForCombo(chosen, oldSubStates);
        } else {
          s.selectedComboVariantId = undefined;
          s.subStates = [];
        }
      }
      return next;
    });
  };

  const setComboSubVariant = (itemIdx: number, subIdx: number, groupId: string, variant: ItemVariant | undefined) => {
    setPerItemState(prev => {
      const next = [...prev];
      const s = next[itemIdx];
      if (s?.type === 'combo') {
        s.subStates = s.subStates.map((ss: any, i: number) => {
          if (i !== subIdx) return ss;
          return { ...ss, variantsByGroup: { ...ss.variantsByGroup, [groupId]: variant } };
        });
      }
      return next;
    });
  };

  const toggleComboSubIngredient = (itemIdx: number, subIdx: number, ingredientId: string, checked: boolean) => {
    setPerItemState(prev => {
      const next = [...prev];
      const s = next[itemIdx];
      if (s?.type === 'combo') {
        s.subStates = s.subStates.map((ss: any, i: number) => {
          if (i !== subIdx) return ss;
          return {
            ...ss,
            SelectedIngredientOptions: ss.SelectedIngredientOptions.map((ing: SelectedIngredientOption) =>
              ing.option.id === ingredientId ? { ...ing, quantity: checked ? 1 : 0 } : ing
            )
          };
        });
      }
      return next;
    });
  };

  const handleIngredientChangeSingle = (itemIdx: number, ingredientId: string, checked: boolean) => {
    setPerItemState(prev => {
      const next = [...prev];
      const s = next[itemIdx];
      if (s?.type === 'single') {
        s.SelectedIngredientOptions = s.SelectedIngredientOptions.map((ing: SelectedIngredientOption) =>
          ing.option.id === ingredientId ? { ...ing, quantity: checked ? 1 : 0 } : ing
        );
      }
      return next;
    });
  };

  const setSelectedVariantSingle = (itemIdx: number, groupId: string, variant: ItemVariant | undefined) => {
    setPerItemState(prev => {
      const next = [...prev];
      const s = next[itemIdx];
      if (s?.type === 'single') {
        s.variantsByGroup = { ...s.variantsByGroup, [groupId]: variant };
      }
      return next;
    });
  };

  /* --- check if every normalized item is fully configured --- */
  const isBundleFullyConfigured = useMemo(() => {
    if (!normalizedItems || normalizedItems.length === 0) return false;

    for (let i = 0; i < normalizedItems.length; i++) {
      const n = normalizedItems[i];
      const s = perItemState[i];
      if (!n?.menuItem || !s) return false;

      // NON-COMBO: base must exist and every variant group must have a selection
      if (!n.menuItem.isCombo) {
        const base: BaseItem | undefined = s?.baseItem;
        if (!base) return false;
        for (const group of base.variants) {
          if (!s.variantsByGroup || typeof s.variantsByGroup[group.id] === 'undefined' || s.variantsByGroup[group.id] === null) {
            return false;
          }
        }
        // ingredients are optional so we don't require any ingredient
      } else {
        // COMBO: must have chosen combo variant and each sub-state must be configured
        const chosenComboVariantId: string | undefined = s?.selectedComboVariantId;
        if (!chosenComboVariantId) return false;
        const chosenCombo = n.comboVariants.find((cv: ComboVariant) => cv.id === chosenComboVariantId);
        if (!chosenCombo) return false;

        const subs = s.subStates || [];
        // length should match number of items in the combo variant
        if (subs.length !== chosenCombo.itemVariantIds.length) return false;

        for (let subIdx = 0; subIdx < subs.length; subIdx++) {
          const ss = subs[subIdx];
          const base: BaseItem | undefined = ss?.baseItem;
          if (!base) return false;
          // require a selected variant for every variant-group of the sub base
          for (const group of base.variants) {
            if (!ss.variantsByGroup || typeof ss.variantsByGroup[group.id] === 'undefined' || ss.variantsByGroup[group.id] === null) {
              return false;
            }
          }
          // ingredients optional
        }
      }
    }

    return true;
  }, [normalizedItems, perItemState]);

  const getMenuCartItems = () => {
    if (!isBundleFullyConfigured) {
      // guard: do not proceed
      // UI already communicates this via disabled button and helper text, but abort if function called programmatically
      return;
    }

    // build an array of CartItem objects (one per normalized item) using toCart
    const cartSelectionItems: MenuCartItem[] = [];
    const bundleRaw = menuData.menu.bundles.find(bun => bun.id == bundle.id)

    normalizedItems.forEach((n, i) => {
      const st = perItemState[i];

      // skip if no menuItem or state
      if (!n?.menuItem || !st) return;

      // COMBO case
      if (n.menuItem.isCombo) {
        // ensure a combo variant was chosen
        const chosenComboVariantId: string | undefined = st.selectedComboVariantId;
        if (!chosenComboVariantId) return;

        const comboVariant = n.comboVariants.find((cv: ComboVariant) => cv.id === chosenComboVariantId);
        if (!comboVariant) return;

        // build comboSelections keyed by baseItem.id
        const comboSelections: Record<string, {
          variantsByGroupId: Record<string, ItemVariant | undefined>;
          ingredients: SelectedIngredientOption[];
        }> = {};

        (st.subStates || []).forEach((ss: any) => {
          const base: BaseItem | undefined = ss.baseItem;
          if (!base || !base.id) return;

          comboSelections[base.id] = {
            variantsByGroupId: ss.variantsByGroup || {},
            ingredients: (ss.SelectedIngredientOptions || []).filter((ing: SelectedIngredientOption) => (ing.quantity || 0) > 0)
          };
        });

        // call toCart for this combo menuItem
        const cartItem = toMenuCart({
          menuItem: n.menuItem,
          comboVariant,
          menuData,
          bundle: bundleRaw,
          comboSelections,
          // baseItem param is required by signature but not used for combo output - pass the first resolved base if available
          baseItem: n.baseItem as BaseItem,
          selectedVariantsByGroup: {}, // not needed for combo path in toCart
          selectedIngredientOptions: [] // not needed for combo path in toCart
        });

        if (cartItem) cartSelectionItems.push(cartItem);
      } else {
        // SINGLE (non-combo) case
        const selectedVariantsByGroup: Record<string, ItemVariant | undefined> = st.variantsByGroup || {};
        const selectedIngredientOptions: SelectedIngredientOption[] = (st.SelectedIngredientOptions || []).filter(
          (ing: SelectedIngredientOption) => (ing.quantity || 0) > 0
        );
        const cartItem = toMenuCart({
          menuItem: n.menuItem,
          comboVariant: null,
          bundle: bundleRaw,
          menuData,
          comboSelections: {}, // not used for single
          baseItem: n.baseItem as BaseItem,
          selectedVariantsByGroup,
          selectedIngredientOptions
        });

        if (cartItem) cartSelectionItems.push(cartItem);
      }
    });

    // Note: ItemCustomizationModal calls onAddToCart with an array of CartItem.
    // Follow that convention here and pass the bundle as a single-element array.
    const allCartItems: MenuCartItem[] = []
    for (let i = 0; i < quantity; i++) {
      cartSelectionItems.forEach(csi => allCartItems.push(csi))
    }
    return allCartItems
  }


  /* --- create cart payload --- */
  const handleAddToCart = () => {

    // mark that the user attempted to add (used to show validation hint)
    setShowValidationAttempt(true);

    if (!isBundleFullyConfigured) {
      // guard: do not proceed
      // UI already communicates this via disabled button and helper text, but abort if function called programmatically
      return;
    }

    const allCartItems = getMenuCartItems() ?? []

    onAddToCart(allCartItems);
    onClose();
  };

  // total price calculation (compute per base item selection)
  const totalPrice = useMemo(() => {
    const cartItems = getMenuCartItems()
    if (!cartItems) return 0
    return getPriceOfMenuCart(cartItems, menuData)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [normalizedItems, perItemState, quantity]);


  /* --- render --- */
  return (
    <Dialog open={isOpen} onClose={onClose} scroll="body" fullWidth maxWidth="md">
      <DialogTitle>
        {bundle.imageUrl && <img src={bundle.imageUrl} alt="Preview" style={{ width: '100%' }} />}
        {bundle.name}
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" sx={{ mb: 1 }}>{bundle.description}</Typography>

        {normalizedItems.map((n, idx) => {
          const state = perItemState[idx] as any;
          if (!n.menuItem) return null;
          const headerImage = n.menuItem.imageUrl ? n.menuItem.imageUrl :
            (n.baseItem && n.baseItem.imageUrl ? n.baseItem.imageUrl : undefined)
          return (
            <div key={n.id} style={{ marginBottom: '1rem', padding: '0.5rem', border: '1px solid #eee', borderRadius: 8 }}>
              <Box sx={{ display: "flex", flexDirection: "row", justifyContent: "space-between", alignItems: 'center' }}>
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  <Typography variant="h6">{n.menuItem.isCombo ? `${n.baseItem?.name} (Combo)` : `${n.baseItem?.name}`} - {formatMoney(n.bundlePrice / 100)}</Typography>

                  {/* If the menuItem is a combo, show radio choices for combo variants */}
                  {n.menuItem.isCombo && n.comboVariants.length > 0 && (
                    <>
                      <Typography variant="subtitle2" sx={{ mt: 1 }}>Choose combo variant</Typography>
                      <RadioGroup
                        value={state?.selectedComboVariantId || ''}
                        onChange={(e) => setSelectedComboVariant(idx, e.target.value || undefined)}
                      >
                        <FormControlLabel value={''} control={<Radio />} label="— Choose variant —" />
                        {n.comboVariants.map(cv => {
                          const priceOverride = n.variantPriceOverrides.find(val => val.variantId == cv.id)
                          if (!isComboVariantSoldOut(cv, menuData)) {
                            return (
                              <FormControlLabel
                                key={cv.id}
                                value={cv.id}
                                control={<Radio />}
                                label={`${cv.name} (${cv.itemVariantIds.length} items) ${priceOverride && "- " + formatMoney(priceOverride.price / 100)}`}
                              />)
                          }
                          return <Typography variant="subtitle1" sx={{ textDecoration: 'line-through' }}>
                            {`X ${cv.name} (${cv.itemVariantIds.length} items) ${priceOverride && "- " + formatMoney(priceOverride.price / 100)} - sold out`}</Typography>
                        })}
                      </RadioGroup>
                    </>
                  )}
                </Box>
                {
                  headerImage &&
                  <img src={headerImage} alt="Preview" style={{ width: '128px', height: '128px' }} />
                }
              </Box>

              {/* If a combo variant has been chosen, render sub-state UI */}
              {n.menuItem.isCombo && state?.selectedComboVariantId ? (
                (() => {
                  const chosen = n.comboVariants.find((cv: ComboVariant) => cv.id === state.selectedComboVariantId);
                  if (!chosen) return <Typography variant="body2">Selected variant not found</Typography>;
                  return (
                    <ManageComboVariant
                      comboVariant={chosen}
                      menuData={menuData}
                      perSubStates={state.subStates || []}
                      onSetSubVariant={(subIdx, groupId, v) => setComboSubVariant(idx, subIdx, groupId, v)}
                      onToggleSubIngredient={(subIdx, ingredientId, checked) => toggleComboSubIngredient(idx, subIdx, ingredientId, checked)}
                      menuItem={n.menuItem}
                    />
                  );
                })()
              ) : !n.menuItem.isCombo ? (
                // regular single menu item
                <ManageIndividualItem
                  baseItem={n.baseItem as BaseItem}
                  menuItem={n.menuItem}
                  variantsByGroup={state?.variantsByGroup || {}}
                  setVariantForGroup={(groupId, v) => setSelectedVariantSingle(idx, groupId, v)}
                  SelectedIngredientOptions={state?.SelectedIngredientOptions || []}
                  handleIngredientChange={(ingredientId, checked) => handleIngredientChangeSingle(idx, ingredientId, checked)}
                />
              ) : (
                // combo but no variant selected yet
                <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>Please choose a combo variant to continue</Typography>
              )}

            </div>
          );
        })}

        <div style={{ display: 'flex', alignItems: 'center', marginTop: '1rem' }}>
          <IconButton onClick={() => setQuantity(q => Math.max(1, q - 1))}>
            <RemoveCircleOutline />
          </IconButton>
          <Typography style={{ margin: '0 1rem' }}>{quantity}</Typography>
          <IconButton onClick={() => setQuantity(q => q + 1)}>
            <AddCircleOutline />
          </IconButton>
          <Typography variant="body2" sx={{ ml: 2 }}>Bundles</Typography>
        </div>
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <Button sx={{ margin: '0 8px', border: '1px solid #162C1F', borderRadius: '24px' }} onClick={onClose}>
          <Typography variant="button" component="h1" sx={{ color: "#162C1F" }}>Cancel</Typography>
        </Button>

        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', width: '100%' }}>
          {/* show validation hint once user attempts to add or if they try to proceed */}
          {!isBundleFullyConfigured && showValidationAttempt && (
            <Typography variant="caption" sx={{ color: 'error.main', mb: 1 }}>
              Please configure every item in the bundle before adding to cart.
            </Typography>
          )}

          <Button
            sx={{ margin: '0 8px', background: "#162C1F", border: "1px solid #162C1F", borderRadius: "24px" }}
            onClick={handleAddToCart}
            variant="contained"
            color="primary"
            disabled={!isBundleFullyConfigured}
          >
            Add to Cart - {formatMoney(totalPrice / 100)}
          </Button>
        </Box>
      </DialogActions>
    </Dialog >
  );
}

export default BundleCustomizationModal;
